/**
 * メモ入力シート
 *
 * BottomSheet形式でメモを入力・作成
 * FAB、時間区切り、Visitカード、ReadCardから共通で利用
 */

import { format } from 'date-fns'
import { Loader2, NotebookPen } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { createTimelineNote } from '@/api/services/overland'
import type { TimelineNote } from '@/api/types/overland'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { useSubmitLock } from '@/hooks/useSubmitLock'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** メモのタイムスタンプ初期値 (ISO 8601) */
  initialTimestamp: string
  /** Visit紐付け */
  visitId?: number | null
  /** Episode紐付け */
  episodeId?: number | null
  /** シートタイトル */
  title?: string
  /** 作成成功時：作成されたメモを返す */
  onCreated?: (note: TimelineNote) => void
}

export const NoteInputSheet = ({
  open,
  onOpenChange,
  initialTimestamp,
  visitId,
  episodeId,
  title = 'メモを追加',
  onCreated,
}: Props) => {
  const [content, setContent] = useState('')
  const [timestamp, setTimestamp] = useState('')
  const { isSubmitting: isSaving, run } = useSubmitLock()

  // シートが開いたときに初期値をセット
  useEffect(() => {
    if (open) {
      setContent('')
      try {
        const dt = new Date(initialTimestamp)
        setTimestamp(format(dt, "yyyy-MM-dd'T'HH:mm"))
      } catch {
        setTimestamp(format(new Date(), "yyyy-MM-dd'T'HH:mm"))
      }
    }
  }, [open, initialTimestamp])

  const handleSave = useCallback(async () => {
    if (!content.trim()) return
    await run(async () => {
      try {
        const ts = new Date(timestamp)
        const created = await createTimelineNote({
          timestamp: ts.toISOString(),
          content: content.trim(),
          visit_id: visitId ?? null,
          episode_id: episodeId ?? null,
        })
        toast.success('メモを追加しました')
        onOpenChange(false)
        onCreated?.(created)
      } catch {
        toast.error('メモの作成に失敗しました')
      }
    })
  }, [content, timestamp, visitId, episodeId, onOpenChange, onCreated, run])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <NotebookPen className="size-4" />
            {title}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-4 p-4">
          {/* 時刻入力 */}
          <div>
            <label htmlFor="note-timestamp" className="text-sm text-muted-foreground mb-1 block">
              時刻
            </label>
            <Input
              id="note-timestamp"
              type="datetime-local"
              value={timestamp}
              onChange={(e) => setTimestamp(e.target.value)}
            />
          </div>

          {/* メモ本文 */}
          <div>
            <label htmlFor="note-content" className="text-sm text-muted-foreground mb-1 block">
              メモ
            </label>
            <Textarea
              id="note-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="メモを入力..."
              className="min-h-[120px]"
              autoFocus
            />
          </div>

          {/* 保存ボタン */}
          <Button className="w-full" onClick={handleSave} disabled={isSaving || !content.trim()}>
            {isSaving ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" />
                保存中...
              </>
            ) : (
              '保存'
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
